import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { adminGetListingDetail, adminApproveListing, adminRejectListing } from "../../../redux/actions/secondaryMarketActions";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";

const SecondaryListingView = () => {
  const { listingId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { listingDetail } = useSelector((state) => state.secondaryMarket);
  const listing = listingDetail.data;

  useEffect(() => {
    dispatch(adminGetListingDetail(listingId));
  }, [dispatch, listingId]);

  const formatCurrency = (amt) => {
    if (amt == null) return "—";
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0 }).format(amt);
  };

  const handleApprove = () => {
    Swal.fire({ title: "Approve?", icon: "question", showCancelButton: true, confirmButtonText: "Approve" }).then((r) => {
      if (r.isConfirmed) {
        dispatch(adminApproveListing(listingId))
          .then(() => { Swal.fire("Approved!", "", "success"); dispatch(adminGetListingDetail(listingId)); })
          .catch(() => Swal.fire("Error", "Failed", "error"));
      }
    });
  };

  const handleReject = () => {
    Swal.fire({ title: "Reject?", input: "text", inputLabel: "Reason", showCancelButton: true, confirmButtonText: "Reject", confirmButtonColor: "#d33" }).then((r) => {
      if (r.isConfirmed) {
        dispatch(adminRejectListing(listingId, r.value || ""))
          .then(() => { Swal.fire("Rejected", "", "info"); navigate("/secondary-listings"); })
          .catch(() => Swal.fire("Error", "Failed", "error"));
      }
    });
  };

  if (listingDetail.loading) return <div className="list"><div className="listContainer"><p style={{ padding: 20 }}>Loading...</p></div></div>;

  if (!listing) return <div className="list"><div className="listContainer"><p style={{ padding: 20 }}>Listing not found</p></div></div>;

  const campaign = listing.Campaign || {};
  const seller = listing.Seller || {};
  const bids = listing.Bids || [];

  return (
    <PageShell>
      <PageHead
        title={`Listing #${listing.id} — ${campaign.fund_name || campaign.corporation_legal_name || ""}`}
        actions={listing.status === "PENDING_REVIEW" ? (
          <>
            <button onClick={handleApprove} className="btn btn--primary">Approve</button>
            <button onClick={handleReject} className="btn btn--danger">Reject</button>
          </>
        ) : null}
      />
      <div className="list">
      <div className="listContainer">
        <div style={{ padding: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
            <div style={{ background: "#f8f9fa", padding: "16px", borderRadius: "8px" }}>
              <h4 style={{ marginTop: 0 }}>Listing Details</h4>
              <p><strong>Status:</strong> {listing.status}</p>
              <p><strong>Type:</strong> {listing.listing_type}</p>
              <p><strong>Asking Price:</strong> {formatCurrency(listing.asking_price)}</p>
              <p><strong>Shares:</strong> {listing.shares_quantity}</p>
              <p><strong>Valuation at Listing:</strong> {formatCurrency(listing.valuation_at_listing)}</p>
              <p><strong>Created:</strong> {new Date(listing.createdAt).toLocaleString()}</p>
              {listing.expires_at && <p><strong>Expires:</strong> {new Date(listing.expires_at).toLocaleString()}</p>}
              {listing.description && <p><strong>Description:</strong> {listing.description}</p>}
              <p><strong>Compliance Acknowledged:</strong>{" "}
                <span style={{
                  padding: "2px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 600,
                  background: listing.compliance_acknowledged ? "#e8f5e9" : "#ffebee",
                  color: listing.compliance_acknowledged ? "#2e7d32" : "#c62828",
                }}>
                  {listing.compliance_acknowledged ? "Yes" : "No"}
                </span>
              </p>
            </div>
            <div style={{ background: "#f8f9fa", padding: "16px", borderRadius: "8px" }}>
              <h4 style={{ marginTop: 0 }}>Seller</h4>
              <p><strong>Name:</strong> {seller.firstname} {seller.lastname}</p>
              <p><strong>Email:</strong> {seller.email}</p>
              <p><strong>User ID:</strong> {seller.id}</p>
            </div>
          </div>

          <h3>Bids ({bids.length})</h3>
          {bids.length === 0 ? (
            <p style={{ color: "#6c757d" }}>No bids on this listing.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f0f0f0", textAlign: "left" }}>
                  <th style={{ padding: "8px" }}>ID</th>
                  <th style={{ padding: "8px" }}>Bidder</th>
                  <th style={{ padding: "8px" }}>Bid Price</th>
                  <th style={{ padding: "8px" }}>Counter Price</th>
                  <th style={{ padding: "8px" }}>Round</th>
                  <th style={{ padding: "8px" }}>Status</th>
                  <th style={{ padding: "8px" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {bids.map((bid) => (
                  <tr key={bid.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: "8px" }}>{bid.id}</td>
                    <td style={{ padding: "8px" }}>{bid.Bidder ? `${bid.Bidder.firstname} ${bid.Bidder.lastname}` : `User #${bid.bidder_id}`}</td>
                    <td style={{ padding: "8px" }}>{formatCurrency(bid.bid_price)}</td>
                    <td style={{ padding: "8px" }}>{bid.counter_price ? formatCurrency(bid.counter_price) : "—"}</td>
                    <td style={{ padding: "8px" }}>{bid.counter_round || 0}</td>
                    <td style={{ padding: "8px" }}>{bid.status}</td>
                    <td style={{ padding: "8px" }}>{new Date(bid.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {listing.activity_logs && listing.activity_logs.length > 0 && (
            <>
              <h3 style={{ marginTop: "24px" }}>Activity Log</h3>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f0f0f0", textAlign: "left" }}>
                    <th style={{ padding: "8px" }}>Action</th>
                    <th style={{ padding: "8px" }}>Actor ID</th>
                    <th style={{ padding: "8px" }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {listing.activity_logs.map((log) => (
                    <tr key={log.id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "8px" }}>{log.action}</td>
                      <td style={{ padding: "8px" }}>{log.actor_id}</td>
                      <td style={{ padding: "8px" }}>{new Date(log.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
      </div>
    </PageShell>
  );
};

export default SecondaryListingView;
